/**
 * ChannelsPanel (TASK-223 Phase 3 / TASK-226 UX / TASK-233 Phase 4 / TASK-251)
 *
 * Renders the user's template channels dynamically from each channel's
 * `config_schema`. TASK-251 collapses the old incoming/outgoing tab split into a
 * SINGLE unified surface: every channel renders here as a route-card, grouped by
 * direction under human subheaders, behind filter-chips (all / incoming /
 * outgoing). The per-channel UI (collapsible expander, dynamic fields, route
 * header, auto-prompt, Telegram widget, save) lives in {@link ChannelSection}.
 *
 * `self` indicates the panel is rendered for the CURRENT user (self-service
 * "Мои каналы"). It is forwarded to ChannelSection → TelegramLinkWidget which
 * can only act on the current user.
 */
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import { usersApi } from '@/services/api/users';
import { useEventsMap } from '@/hooks/useEventsMap';
import { Button } from '@/components/ui/button';
import type { User, UserChannel } from '@/types/user';
import { ChannelSection } from './ChannelSection';

interface ChannelsPanelProps {
  user: User;
  /**
   * True when rendered for the current user (self-service "Мои каналы").
   * Enables the full Telegram link/unlink flow for the telegram_system channel.
   * Defaults to false (admin editing someone else → read-only telegram status).
   */
  self?: boolean;
  /**
   * KAN-752: show only these channel codenames, as plain cards without the
   * direction filter and group headers (operator page, "Telegram" tab).
   */
  only?: readonly string[];
}

type DirectionFilter = 'all' | 'incoming' | 'outgoing';

/** A channel is OUTGOING only when direction === 'outgoing'; else incoming. */
function isOutgoing(channel: UserChannel): boolean {
  return channel.direction === 'outgoing';
}

/**
 * Bridge one user channel onto the reusable ChannelSection, wiring the
 * user-scoped save endpoint. Lives in its own component so the save callback is
 * stable per channel (useCallback keyed by userId + codename).
 */
function UserChannelSection({
  channel,
  userId,
  self,
  telegramLinked,
  telegramUsername,
  eventsMap,
  autoCallbackEnabled,
  canManageAutoCallback,
  operatorNameMissing,
}: {
  channel: UserChannel;
  userId: string;
  self: boolean;
  telegramLinked?: boolean;
  telegramUsername?: string | null;
  eventsMap?: Record<string, string>;
  /** TASK-336: seed for the voice auto-callback toggle (User field). */
  autoCallbackEnabled?: boolean;
  /**
   * TASK-347: whether the operator's «Имя для клиентов» is empty. Forwarded to
   * ChannelSection so a client-facing channel cannot be enabled without a name.
   */
  operatorNameMissing?: boolean;
  /**
   * TASK-336 review №2: whether the CURRENT user may persist the auto-callback
   * flag (PATCH /users/{id}/ requires `users.manage`). Without it the toggle
   * is not rendered at all — passing the handler down would show a switch that
   * always 403s and silently reverts.
   */
  canManageAutoCallback: boolean;
}): JSX.Element {
  const handleSave = useCallback(
    (body: { enabled: boolean; values: Record<string, unknown> }) =>
      usersApi.updateChannel(userId, channel.codename, body),
    [userId, channel.codename]
  );

  // TASK-336: the auto-callback flag is a USER field, so it persists via
  // PATCH /users/{id}/ — deliberately NOT through the channel values payload.
  // ChannelSection renders the toggle for the voice channel only.
  const handleAutoCallbackChange = useCallback(
    (enabled: boolean) =>
      usersApi.update(userId, { auto_callback_enabled: enabled }),
    [userId]
  );

  return (
    <ChannelSection
      channel={channel}
      onSave={handleSave}
      userId={userId}
      self={self}
      telegramLinked={telegramLinked}
      telegramUsername={telegramUsername}
      eventsMap={eventsMap}
      autoCallbackEnabled={autoCallbackEnabled}
      onAutoCallbackChange={
        canManageAutoCallback ? handleAutoCallbackChange : undefined
      }
      operatorNameMissing={operatorNameMissing}
    />
  );
}

export function ChannelsPanel({
  user,
  self = false,
  only,
}: ChannelsPanelProps): JSX.Element {
  const allChannels = user.channels ?? [];

  const [filter, setFilter] = useState<DirectionFilter>('all');

  // TASK-336 review №2 (RBAC): the auto-callback flag persists via PATCH
  // /users/{id}/, which requires the `users.manage` permission. The project
  // convention for manage-only controls is HIDE, not disable (see DatasetsPage
  // / UsersPage), so without the permission the toggle is not rendered at all.
  // Shares the ['currentUser'] query key used across pages (cached once).
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const canManageUsers = currentUser?.permissions?.users?.manage === true;

  // Resolve event codenames → human labels for outgoing route headers. Shares
  // the ['events'] query key with ChannelSection's EventsField (cached once).
  const eventsMap = useEventsMap();

  // TASK-347: the operator's «Имя для клиентов» is missing when empty/whitespace.
  // ChannelSection uses this to block enabling any client-facing channel.
  const operatorNameMissing = !user.operator_display_name?.trim();

  const incoming = allChannels.filter((ch) => !isOutgoing(ch));
  const outgoing = allChannels.filter((ch) => isOutgoing(ch));

  const showIncoming = filter !== 'outgoing' && incoming.length > 0;
  const showOutgoing = filter !== 'incoming' && outgoing.length > 0;

  const renderChannel = (channel: UserChannel): JSX.Element => (
    <UserChannelSection
      key={channel.codename}
      channel={channel}
      userId={user.id}
      self={self}
      telegramLinked={user.telegram_linked}
      telegramUsername={user.telegram_username}
      eventsMap={eventsMap}
      autoCallbackEnabled={user.auto_callback_enabled}
      canManageAutoCallback={canManageUsers}
      operatorNameMissing={operatorNameMissing}
    />
  );

  if (only) {
    const picked = allChannels.filter((ch) => only.includes(ch.codename));
    return (
      <div data-testid="channels-panel" className="space-y-4">
        {picked.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            У выбранного шаблона нет этого канала.
          </p>
        ) : (
          picked.map(renderChannel)
        )}
      </div>
    );
  }

  return (
    <div data-testid="channels-panel" className="space-y-4">
      {allChannels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          У выбранного шаблона нет настраиваемых каналов.
        </p>
      ) : (
        <>
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              data-testid="channel-filter-all"
              variant={filter === 'all' ? 'default' : 'outline'}
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}>
              Все
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="channel-filter-incoming"
              variant={filter === 'incoming' ? 'default' : 'outline'}
              aria-pressed={filter === 'incoming'}
              onClick={() => setFilter('incoming')}>
              ← Входящие
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="channel-filter-outgoing"
              variant={filter === 'outgoing' ? 'default' : 'outline'}
              aria-pressed={filter === 'outgoing'}
              onClick={() => setFilter('outgoing')}>
              → Исходящие
            </Button>
          </div>

          {/* Incoming group */}
          {showIncoming && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">
                Входящие · клиент разговаривает с ботом
              </h4>
              <div className="space-y-4">{incoming.map(renderChannel)}</div>
            </div>
          )}

          {/* Outgoing group */}
          {showOutgoing && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">
                Исходящие · система оповещает по событиям
              </h4>
              <div className="space-y-4">{outgoing.map(renderChannel)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChannelsPanel;
